"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Moon, Sun, LayoutDashboard, Settings, Users, BookOpen, BarChart3, UserCircle } from "lucide-react";
import { useAuthStore, menuItems } from "@repo/shared";
import { useTheme } from "next-themes";

const roleBadge: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  "super-admin": { label: "Super Admin", variant: "default" },
  admin: { label: "Admin", variant: "secondary" },
  user: { label: "User", variant: "outline" },
};

export function AppHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const { user, logout, isAdmin, hasRole } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const admin = isAdmin();
  const superAdmin = hasRole("super-admin");

  if (!user) return null;

  const initials = user.username
    .slice(0, 2)
    .toUpperCase();

  const badge = roleBadge[user.role] ?? { label: user.role, variant: "outline" as const };

  const iconBtnClass = (active: boolean) =>
    `inline-flex items-center justify-center rounded-md p-2 transition-colors ${
      active
        ? "bg-accent text-accent-foreground"
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    }`;

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      {/* Current page title */}
      <span className="text-sm font-medium truncate">
        {(() => {
          if (pathname === "/dashboard") return "Dashboard";
          if (pathname === "/settings") return "Settings";
          if (pathname === "/users") return "User Management";
          if (pathname === "/changelog") return "Version History";
          if (pathname === "/documentation") return "API Documentation";
          if (pathname === "/statistics") return "KSeF Statistics";
          if (pathname === "/profile") return "Profile";
          const flat = menuItems.flatMap((m) => m.children ? m.children : [m]);
          return flat.find((m) => m.href && pathname.startsWith(m.href))?.label ?? "";
        })()}
      </span>

      <div className="flex-1" />

      <TooltipProvider delayDuration={300}>
        {/* Dashboard icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link href="/dashboard" className={iconBtnClass(pathname === "/dashboard")}>
              <LayoutDashboard className="size-4" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>Dashboard</TooltipContent>
        </Tooltip>

        {/* Settings icon (admin and super-admin) */}
        {admin && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link href="/settings" className={iconBtnClass(pathname === "/settings")}>
                <Settings className="size-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>Settings</TooltipContent>
          </Tooltip>
        )}

        {/* KSeF Statistics (all users) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link href="/statistics" className={iconBtnClass(pathname === "/statistics")}>
              <BarChart3 className="size-4" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>KSeF Statistics</TooltipContent>
        </Tooltip>

        {/* API Docs (admin or higher) */}
        {admin && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link href="/documentation" className={iconBtnClass(pathname === "/documentation")}>
                <BookOpen className="size-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>API Documentation</TooltipContent>
          </Tooltip>
        )}

        {/* Theme toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={iconBtnClass(false)}
            >
              <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{theme === "dark" ? "Light mode" : "Dark mode"}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-accent transition-colors">
            <Avatar className="size-8">
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden md:flex flex-col items-start text-sm">
              <span className="font-medium leading-none">{user.username}</span>
              <Badge variant={badge.variant} className="mt-1 text-[10px] px-1.5 py-0">
                {badge.label}
              </Badge>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span>{user.username}</span>
              <span className="text-xs font-normal text-muted-foreground">{badge.label}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => router.push("/profile")}>
            <UserCircle className="size-4 mr-2" />
            Profile & KSeF Certs
          </DropdownMenuItem>
          {admin && (
            <DropdownMenuItem onClick={() => router.push("/users")}>
              <Users className="size-4 mr-2" />
              User Management
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { logout(); sessionStorage.removeItem("efakt-auth-backup"); router.push("/login"); }}>
            <LogOut className="size-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
